import React, {useEffect, useState} from 'react'
import _ from 'lodash'
import dayjs from 'dayjs'
import {Button, Input, message, Modal} from 'antd'
import {Formik} from 'formik'
import useAsync from 'modules/asyncCache/useAsync'
import useAsyncAction from 'modules/asyncCache/useAsyncAction'
import {ChipGroup, Field, FormActions, Section} from 'components/form/ModernForm'
import {LoadingOutlined} from '@ant-design/icons'
import {IoClose} from 'react-icons/io5'
import {RiSearch2Line} from 'react-icons/ri'
import UserLookupComponent from 'components/UserLookupComponent'
import {familyMomentNotice_createAndSend_Api, familyMomentNotice_suggestRecipients_Api,} from 'apis/familyMomentNotice'
import {InfoBanner, MOMENT_META} from './MomentRow'

const MESSAGE_INTENT_OPTIONS = [
    {label: 'moment_intent_text', value: 'text'},
    {label: 'moment_intent_prayer', value: 'prayer'},
    {label: 'moment_intent_gathering', value: 'gathering'},
]

const AUDIENCE_SCOPE_OPTIONS = [
    {label: 'moment_scope_book', value: 'book'},
    {label: 'moment_scope_forest', value: 'forest'},
]

// Soạn + gửi thông báo cho 1 "kỷ niệm" - luôn gắn với 1 thành viên cụ thể (item: {member_id,
// member_name, type?, date?, source_date?}). `item.type` có -> occurrence lấy từ MomentRow (list
// "sắp đến"), cho phép đổi sang "dịp tự đặt tên" (vd Lễ Thôi Nôi) qua nút toggle. `item.type` KHÔNG
// có (mở từ nút "+ Thêm mới", MomentsPanel) -> luôn ở chế độ tự đặt tên, không có nút toggle (không
// có loại chuẩn nào để quay lại).
// `audience_scope` (Gia đình nhỏ/cùng sổ vs Toàn dòng họ/cả rừng liên kết) chỉ đổi DANH SÁCH GỢI Ý
// người nhận (suggestRecipients?scope=...) - đổi scope thì load lại gợi ý, reset lựa chọn đã tick.
const SendMomentNoticeModal = ({item, onClose, onSuccess, t}) => {
    const [useCustomTitle, setUseCustomTitle] = useState(!item.type)
    const [audienceScope, setAudienceScope] = useState('book')
    const [selectedEmails, setSelectedEmails] = useState(null)
    // Người nhận thêm NGOÀI danh sách gợi ý tự động (vd thân nhân đúng ra thuộc diện gợi ý nhưng lọt
    // ngoài scope đang chọn, hoặc chưa liên kết đúng bản ghi thành viên) - LUÔN tra qua
    // UserLookupComponent (khớp CHÍNH XÁC email/mã định danh, không cho gõ email tự do) để email lấy
    // đúng từ tài khoản Kristo thật, đồng nhất cách "suggestRecipients" lấy email qua memberUser -
    // không tạo thêm 1 nguồn email tự gõ tay dễ sai/không xác minh được.
    const [extraRecipients, setExtraRecipients] = useState([])

    // `values` (KHÔNG phải `query`) - path của API này không có placeholder ":xxx" nào, filter thật
    // sự đi vào querystring GET phải qua values + paramsFields (xem callApi.js: search string build
    // từ values[key], không phải query[key] - query chỉ để thay ":xxx" trong URL path). Dùng nhầm
    // `query` khiến family_book_member_id KHÔNG được gửi lên -> BE nhận null -> lỗi "family book
    // member not exists" (bug thực tế đã gặp).
    const {response, isLoading} = useAsync({
        apiInfo: familyMomentNotice_suggestRecipients_Api,
        values: {family_book_member_id: item.member_id, scope: audienceScope},
        deps: [item.member_id, audienceScope],
    })
    const suggestions = _.get(response, 'data.data', [])

    // Tick sẵn hết gợi ý lúc load xong (hoặc lúc đổi scope) - người gửi bỏ chọn/không chọn hết trước
    // khi gửi (đã chốt: KHÔNG tự động gửi hết không cho chọn).
    useEffect(() => {
        setSelectedEmails(suggestions.length ? suggestions.map((s) => s.email) : [])
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [audienceScope, suggestions.length])

    const {handleAsyncAction: doSend, loading: sending} = useAsyncAction({
        apiInfo: familyMomentNotice_createAndSend_Api,
        onSuccess: () => {
            message.success(t('moment notice sent'))
            onSuccess && onSuccess()
        },
        onError: () => message.error(t('error occurred')),
    })

    const meta = item.type ? (MOMENT_META[item.type] || {label: item.type}) : null

    const initialValues = {
        moment_date: item.date ? dayjs(item.date).format('YYYY-MM-DD') : '',
        custom_title: '',
        location: '',
        message_intent: 'text',
        message: '',
    }

    const validate = (values) => {
        const errors = {}
        if (!values.moment_date) errors.moment_date = t('required field')
        if (useCustomTitle && !values.custom_title) errors.custom_title = t('required field')
        return errors
    }

    const selected = selectedEmails || []
    const toggleRecipient = (email) => setSelectedEmails(
        selected.includes(email) ? selected.filter((e) => e !== email) : [...selected, email]
    )

    const handleAddExtraRecipient = (userInfo) => {
        if (!userInfo?.id || !userInfo?.email) return
        const alreadyThere = suggestions.some((s) => s.email === userInfo.email)
            || extraRecipients.some((r) => r.email === userInfo.email)
        if (alreadyThere) return message.info(t('recipient already in list'))
        setExtraRecipients([...extraRecipients, {user_id: userInfo.id, name: userInfo.name, email: userInfo.email}])
    }
    const handleRemoveExtraRecipient = (email) => setExtraRecipients(extraRecipients.filter((r) => r.email !== email))

    return (
        <Modal
            width={420}
            className="custom-modal"
            rootClassName="family-theme"
            open={true}
            title={<div className="text-center font-bold text-color-000 uppercase tracking-wide">{t('send moment notice')}</div>}
            destroyOnHidden
            footer={null}
            onCancel={onClose}>
            <Formik
                initialValues={initialValues}
                validate={validate}
                validateOnMount
                onSubmit={(values) => {
                    if (!selected.length && !extraRecipients.length) return message.warning(t('choose at least 1 recipient'))
                    doSend({
                        family_book_member_id: item.member_id,
                        moment_type: useCustomTitle ? undefined : item.type,
                        custom_title: useCustomTitle ? values.custom_title : undefined,
                        moment_date: values.moment_date,
                        location: values.location || undefined,
                        audience_scope: audienceScope,
                        message_intent: values.message_intent,
                        message: values.message || undefined,
                        // user_id: BẮT BUỘC gửi kèm (trước đây thiếu) - createAndSendNotice() chỉ bắn
                        // thông báo trong app (badge chuông) cho recipient có user_id, thiếu field này
                        // thì email vẫn gửi được nhưng thông báo trong app im lặng không bao giờ chạy.
                        // JSON.stringify - request đi qua createFormData() (FormData.append không
                        // serialize array-of-object, coerce qua toString() -> "[object Object],..."),
                        // field phức tạp phải tự stringify ở FE + json_decode ở BE, đúng convention
                        // "FormData — array/object field phải JSON.stringify" (docs/coding-style/
                        // conventions.md) - bug thực tế đã gặp (lỗi validate "recipients phải là mảng").
                        recipients: JSON.stringify([
                            ...suggestions
                                .filter((s) => selected.includes(s.email))
                                .map((s) => ({family_book_member_id: s.family_book_member_id, user_id: s.user_id, email: s.email})),
                            ...extraRecipients.map((r) => ({user_id: r.user_id, email: r.email})),
                        ]),
                    })
                }}>
                {({values, touched, errors, setFieldValue, handleSubmit, isSubmitting, isValid}) => (
                    <form onSubmit={handleSubmit} className="flex flex-col gap-3 pt-2">
                        <InfoBanner>{t('send_moment_notice_intro')}</InfoBanner>

                        <Section title={t('occasion')} theme="highlight">
                            <div className="text-sm font-semibold text-color-000">{item.member_name}</div>
                            {!useCustomTitle ? (
                                <div className="text-xs text-family-700 font-semibold uppercase tracking-wide">{t(meta.label)}</div>
                            ) : (
                                <Field required label={t('custom occasion title')} error={touched.custom_title && errors.custom_title}>
                                    <Input
                                        value={values.custom_title}
                                        onChange={(e) => setFieldValue('custom_title', e.target.value)}
                                        placeholder={t('custom occasion title placeholder')}
                                    />
                                </Field>
                            )}
                            {/* Chỉ có nút toggle khi mở từ 1 occurrence chuẩn (item.type có) - mở từ
                                "+ Thêm mới" thì không có loại chuẩn nào để quay lại. */}
                            {item.type && (
                                <button
                                    type="button"
                                    onClick={() => setUseCustomTitle((v) => !v)}
                                    className="self-start text-2xs font-semibold text-family-700 underline cursor-pointer bg-transparent border-0 p-0">
                                    {useCustomTitle ? t('use standard occasion type') : t('this is a different occasion (eg full-month ceremony)')}
                                </button>
                            )}
                            <Field required label={t('occasion date')} error={touched.moment_date && errors.moment_date}>
                                <Input
                                    type="date"
                                    value={values.moment_date}
                                    onChange={(e) => setFieldValue('moment_date', e.target.value)}
                                />
                            </Field>
                            <Field label={t('location')} subtitle={t('optional')}>
                                <Input
                                    value={values.location}
                                    onChange={(e) => setFieldValue('location', e.target.value)}
                                    placeholder={t('location placeholder')}
                                />
                            </Field>
                        </Section>

                        <Section title={t('message intent')}>
                            <ChipGroup
                                required
                                options={MESSAGE_INTENT_OPTIONS}
                                value={values.message_intent}
                                onChange={(v) => setFieldValue('message_intent', v)}
                            />
                            <Input.TextArea
                                rows={3}
                                value={values.message}
                                onChange={(e) => setFieldValue('message', e.target.value)}
                                placeholder={t('moment notice message placeholder')}
                            />
                        </Section>

                        <Section title={t('recipients')}>
                            <Field label={t('audience scope')}>
                                <ChipGroup
                                    required
                                    options={AUDIENCE_SCOPE_OPTIONS}
                                    value={audienceScope}
                                    onChange={setAudienceScope}
                                />
                            </Field>
                            {isLoading && !suggestions.length ? (
                                <div className="flex items-center justify-center py-4"><LoadingOutlined className="text-family-500"/></div>
                            ) : suggestions.length ? (
                                <div className="flex flex-col gap-1.5">
                                    {suggestions.map((s) => (
                                        <label
                                            key={s.email}
                                            className="p-2 flex items-center gap-2 rounded-lg border border-color-50 cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={selected.includes(s.email)}
                                                onChange={() => toggleRecipient(s.email)}
                                            />
                                            <div className="flex-1 min-w-0">
                                                <div className="text-xs font-semibold text-color-000 truncate">{s.name}</div>
                                                <div className="text-2xs text-color-400 truncate">{s.email}</div>
                                            </div>
                                        </label>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-xs text-color-300 italic">{t('no recipient suggestions found')}</p>
                            )}

                            {/* Người nhận thêm tay - hiện dạng tag, gỡ được từng người. Email luôn lấy
                                qua tài khoản Kristo tra được (UserLookupComponent), không cho gõ tự do. */}
                            {extraRecipients.length > 0 && (
                                <div className="flex flex-wrap gap-1.5">
                                    {extraRecipients.map((r) => (
                                        <span
                                            key={r.email}
                                            className="pl-2.5 pr-1.5 py-1 flex items-center gap-1.5 rounded-full bg-family-50 border border-family-200 text-2xs font-semibold text-family-700">
                                            {r.name}
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveExtraRecipient(r.email)}
                                                className="flex flex-center rounded-full hover:bg-family-100 cursor-pointer bg-transparent border-0 p-0.5">
                                                <IoClose size={11}/>
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}

                            <UserLookupComponent handleApply={handleAddExtraRecipient}>
                                <Button
                                    type="dashed"
                                    icon={<RiSearch2Line size={13}/>}
                                    className="w-full flex items-center justify-center gap-1.5 text-xs">
                                    {t('add moment recipient')}
                                </Button>
                            </UserLookupComponent>
                        </Section>

                        <FormActions
                            onCancel={onClose}
                            submitText="send moment notice"
                            isSubmitting={isSubmitting || sending}
                            isValid={isValid}
                        />
                    </form>
                )}
            </Formik>
        </Modal>
    )
}

export default SendMomentNoticeModal
